import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { isPBSession } from "../../../utils/appSource";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  IonContent,
  IonPage,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import GoogleMapReact from "google-map-react";
import { CircularProgress, TextField } from "@mui/material";
import {
  ArrowLeft,
  Briefcase,
  ChevronRight,
  Crosshair,
  Home,
  Info,
  MapPin,
  MapPinOff,
  MoreVertical,
  Navigation,
  Plus,
  Search,
  User as UserIcon,
  X,
} from "lucide-react";
import {
  getCurrentLocation,
  userMessageForLocationError,
} from "../../../services/LocationService";
import utilityService from "../../../services/UtilityService";
import navigoServices from "../../../services/NavigoServices";
import familyPlanServices from "../../../services/FamilyPlanServices";
import addressService from "../../../services/AddressService";
import {
  checkConfig,
  setProvidersList,
  setStateFromPayload,
} from "../navigoSlice";
import Pin from "../../../components/common/location/Pin";
import { MapPinIcon as MapPinSolidIcon } from "@heroicons/react/24/solid";
import { NavigoFinalSectionContent } from "./navigoFinalSection";
import useUnhidePageOnEnter from "../../../hooks/useUnhidePageOnEnter";
import { backToPath, goBackTo, withFrom } from "../utils/navBack";

// Until we have a location, show the whole country rather than a city we
// guessed at: zoom 4 around India's centroid fits ~8-37N / 68-97E on a
// phone-sized map div.
const DEFAULT_CENTER = { lat: 22.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 4;
const LOCATED_ZOOM = 14;
const RADIUS_PRESETS_KM = [6, 10, 15];
const RADIUS_FALLBACKS_M = [6000, 10000, 15000];
const CUSTOM_RANGE_STOPS_KM = [6, 10, 15, 30, 50, 75, 100];

const FORM_FIELD_SX = {
  "& .MuiInputBase-input": {
    fontFamily: "'Montserrat', sans-serif",
    color: "#12194C",
  },
  "& .MuiInputBase-root": {
    fontFamily: "'Montserrat', sans-serif",
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Montserrat', sans-serif",
    color: "#9CA3AF",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    color: "#374151",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#374151",
  },
  "& .MuiFormLabel-asterisk, & .MuiInputLabel-asterisk": {
    color: "#DC2626",
  },
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Montserrat', sans-serif",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#D1D5DB" },
    "&.Mui-focused fieldset": { borderColor: "#9CA3AF" },
  },
};

async function reverseGeocode(lat, lng) {
  try {
    const res = await utilityService.getFormattedAddressFromCoordinates({
      latitude: lat,
      longitude: lng,
    });
    return res?.data || null;
  } catch {
    return null;
  }
}

function formatAddressLine(d) {
  if (!d) return "";
  const parts = [
    d.housing,
    d.building,
    d.street,
    d.landmark,
    d.subLocality,
    d.localityArea,
  ]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean);
  let line = parts.join(", ");
  const pincode =
    d.pincode != null && String(d.pincode).trim() !== ""
      ? String(d.pincode).trim()
      : "";
  if (pincode) {
    line = (line ? line + " " : "") + "-" + pincode;
  }
  return line;
}

function providerCoordsOf(provider) {
  const geo = provider?.geolocation;
  if (!geo || !Array.isArray(geo.coordinates) || geo.coordinates.length < 2) {
    return null;
  }
  const [lng, lat] = geo.coordinates;
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return { lat, lng };
}

function providersFromResponse(res) {
  const data = res?.data;
  if (Array.isArray(data?.providers)) return data.providers;
  if (Array.isArray(data)) return data;
  return [];
}

function suggestionsFromResponse(res) {
  const data = res?.data;
  if (Array.isArray(data?.predictions)) return data.predictions;
  if (Array.isArray(data?.suggestions)) return data.suggestions;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

function suggestionDisplayText(s) {
  return s?.placePrediction?.text?.text || "";
}

function addressNeedsCoordinatesUpdate(addr) {
  const c = addr?.coordinates;
  if (!c) return true;
  if (c.latitude === 0 && c.longitude === 0) return true;
  return false;
}

function makePlacesSessionToken() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function suggestionCoords(s) {
  const lat = s?.latitude ?? s?.lat ?? s?.coordinates?.latitude;
  const lng = s?.longitude ?? s?.lng ?? s?.coordinates?.longitude;
  if (typeof lat === "number" && typeof lng === "number") {
    return { lat, lng };
  }
  return null;
}

function ProviderMarker({ provider, selected, onClick, onClose }) {
  return (
    <div
      style={{
        position: "absolute",
        transform: "translate(-50%, -50%)",
      }}
    >
      <button
        type="button"
        // Stop the tap before it reaches the map underneath. google-map-react
        // renders markers inside the Google Maps container, so a click on one
        // also bubbles to the map's own click listener — handleMapClick then
        // ran with `selectedProvider` still null for that render, dropped a pin
        // on the provider and refetched the list, so the popup never stayed up.
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        aria-label={provider?.name || "Provider"}
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          backgroundColor: "#97144D",
          color: "#FFFFFF",
          border: "2px solid #FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        }}
      >
        <UserIcon size={14} />
      </button>
      {selected && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            bottom: 40,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#FFFFFF",
            borderRadius: 10,
            padding: "12px 36px 12px 16px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
            fontSize: 14,
            fontWeight: 700,
            color: "#12194C",
            whiteSpace: "nowrap",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          {provider?.name || "Provider"}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 2,
              color: "#6B7280",
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Renders the address form OUTSIDE ion-content, at <body> level.
 *
 * The iOS blank screen on this form is a compositing failure, not a logic bug.
 * ion-content's scroller (the shadow `div.inner-scroll`) carries
 * `-webkit-overflow-scrolling: touch` + `will-change: scroll-position`, and the
 * host carries `contain: size style`. That makes it its own compositing layer,
 * and dragging a caret or selection inside a text field in that layer is a
 * known way to make iOS fail to repaint it: the content is still there, the
 * layer just comes back empty. Android's WebView does not composite this way,
 * which is why it was iOS-only.
 *
 * Two earlier attempts fought the layer with CSS and lost. A `::part(scroll)`
 * rule in Base.scss is inert on Ionic 5 (ion-content gained `part` in v6), and
 * applying the same properties at runtime via getScrollElement() did not help
 * either. Both tried to tame the layer; neither removed it.
 *
 * This removes it. A portal to document.body puts the form outside the shadow
 * scroller entirely, in a plain overflow container with none of those
 * properties, so there is no promoted layer left to fail to repaint. It also
 * takes the inputs out of Ionic's input-shim path (the shim skips anything
 * whose `closest('ion-content')` is null), which is what the
 * `--keyboard-offset: 290px` hack in Base.scss exists to undo.
 *
 * `active` is false everywhere except the address form, and then this returns
 * children untouched - so no other screen, and no other mode of this screen,
 * changes at all.
 */
function AddressFormShell({ active, children }) {
  if (!active) return children;
  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 20,
        backgroundColor: "#FFFFFF",
        // Plain scrolling, deliberately. `-webkit-overflow-scrolling: touch` is
        // what promotes a scroll container to its own layer in the first place.
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "auto",
        // ion-content normally applies these; outside it we do it here, or the
        // header sits under the notch and Save under the home indicator.
        paddingTop: "var(--ion-safe-area-top, env(safe-area-inset-top, 0px))",
        paddingBottom:
          "var(--ion-safe-area-bottom, env(safe-area-inset-bottom, 0px))",
      }}
    >
      {children}
    </div>,
    document.body
  );
}

export default function SelectLocation() {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const routeLocation = useLocation();
  // See providerCard: fall back to the active booking's service in redux when
  // a navigation arrives without route state.
  const currentServicesInStore = useSelector(
    (store) => store.navigo?.ui?.currentServices
  );
  const service = routeLocation?.state?.service || currentServicesInStore;
  const pageType = routeLocation?.state?.pageType;
  const planId = routeLocation?.state?.planId;
  // Address-only mode: reuse the saved-addresses list + add/edit address flow
  // for the "Home sample" booking (no map/providers/radius for picking centres).
  // Derive from the path, not just route state. /select-address exists solely
  // for this mode, and route state does not survive a back navigation — the
  // trail carries the path but not the flag, so returning to this screen
  // rendered it in provider mode ("Search your location") in the middle of the
  // home-sample flow.
  //
  // Also derive it from the booking's own mode. Home sample uses addresses and
  // must never show the provider map, but this screen can be ARRIVED at on
  // /select-location by going back across branches of a session — the two
  // routes render the same component, and a trail from an earlier Centre-visit
  // detour resolves here. Patching each exit kept missing a path in; deciding
  // it from the mode enforces the rule at the screen itself, whichever route
  // was used to reach it.
  const addressOnly =
    routeLocation?.state?.addressOnly === true ||
    routeLocation?.pathname === "/select-address" ||
    service?.serviceModeDetails?.[0]?.modeName === "Home sample";
  // Passed from the "Booking for" screen for the Home-sample slot booking.
  const familyMember = routeLocation?.state?.familyMember;
  const memberRelation = routeLocation?.state?.memberRelation ?? 0;
  const price = routeLocation?.state?.price ?? 0;

  const patientProfile = useSelector(
    (store) => store.login.user.patientProfile
  );

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  // const apiKey = "AIzaSyDAvmzLByEt9PuG8QIM3dFVZbWppkgaL2A" || "";

  const [coords, setCoords] = useState(null);
  const [currentLocationCoords, setCurrentLocationCoords] = useState(null);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [radiusKm, setRadiusKm] = useState(6);
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [pendingCustomKm, setPendingCustomKm] = useState(30);
  const [addressData, setAddressData] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState(null);

  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [noProvidersFound, setNoProvidersFound] = useState(false);
  const [fallbackExhausted, setFallbackExhausted] = useState(false);
  const [fallbackInProgress, setFallbackInProgress] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressMenuOpenId, setAddressMenuOpenId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const addressesFetchedRef = useRef(false);
  const placesSessionTokenRef = useRef(null);

  const [addAddressMode, setAddAddressMode] = useState(null);

  // PB does not get the geolocation features in this release: no map and no
  // place search anywhere in the add-address flow. Geolocation + centre visit
  // are a separate PB release.
  //
  // This is safe to do without a location because coordinates are NOT required
  // to save an address: addressNeedsCoordinatesUpdate() above already treats
  // missing/(0,0) coordinates as a normal state, and the app backfills them
  // server-side from the stored address via
  // addressService.getCoordinatesFromAddress(addressId). PB addresses simply
  // take that existing path.
  const hideLocationFeatures = isPBSession();

  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const addressMenuRef = useRef(null);
  const activeTripleDotRef = useRef(null);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [isDeletingAddress, setIsDeletingAddress] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const initialAddressForm = {
    name: "",
    phone: "",
    housing: "",
    colony: "",
    landmark: "",
    pincode: "",
    city: "",
    district: "",
    state: "",
    addressType: "Home",
  };
  const [addressForm, setAddressForm] = useState(initialAddressForm);

  // Home-sample serviceability check + slot screen (addressOnly flow).
  const [navigoFinalSection, setNavigoFinalSection] = useState(false);
  const [choosedAddressId, setChoosedAddressId] = useState(null);
  const [checkingServiceAddressId, setCheckingServiceAddressId] =
    useState(null);
  const [serviceError, setServiceError] = useState(null);

  const handleSelectAddress = (addr) => {
    if (!addr || checkingServiceAddressId) return;
    setServiceError(null);
    setCheckingServiceAddressId(addr.id);
    dispatch(
      checkConfig({ patientId: patientProfile.id, planId: service?.planId })
    );
    navigoServices
      .checkService({ serviceId: service?.serviceId, addressId: addr.id })
      .then((result) => {
        const res = result?.data;
        if (res && res.serviceable) {
          dispatch(
            setStateFromPayload({
              addressSelected: { ...addr },
              currentServices: { ...service },
            })
          );
          setChoosedAddressId(addr.id);
          setCheckingServiceAddressId(null);
          setNavigoFinalSection(true);
        } else {
          setCheckingServiceAddressId(null);
          setServiceError(
            "Location is non-serviceable. Please select a different address"
          );
        }
      })
      .catch(() => {
        setCheckingServiceAddressId(null);
        setServiceError(
          "Location is non-serviceable. Please select a different address"
        );
      });
  };

  const openSearchOverlay = () => {
    if (searchOverlayOpen) return;
    setSearchOverlayOpen(true);
    if (!placesSessionTokenRef.current) {
      placesSessionTokenRef.current = makePlacesSessionToken();
    }
    // While adding an address the overlay shows suggestions only, so there is
    // no saved-address list to populate.
    if (addAddressMode) return;
    setLoadingAddresses(true);
    familyPlanServices
      .getAllAddress(-1)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setSavedAddresses(list);
      })
      .catch(() => setSavedAddresses([]))
      .finally(() => setLoadingAddresses(false));
  };

  const closeSearchOverlay = () => {
    setSearchOverlayOpen(false);
    setAddressMenuOpenId(null);
    placesSessionTokenRef.current = null;
  };

  const handleSearchClear = () => {
    if (searchOverlayOpen) {
      setSearchQuery("");
      closeSearchOverlay();
    } else {
      setSearchQuery("");
    }
  };

  useEffect(() => {
    if (!searchOverlayOpen) return undefined;
    const q = searchQuery.trim();
    if (!q) {
      setSuggestions([]);
      setLoadingSuggestions(false);
      return undefined;
    }
    setLoadingSuggestions(true);
    const handle = setTimeout(() => {
      utilityService
        .placesAutocomplete({
          input: q,
          sessionToken: placesSessionTokenRef.current,
        })
        .then((res) => setSuggestions(suggestionsFromResponse(res)))
        .catch(() => setSuggestions([]))
        .finally(() => setLoadingSuggestions(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [searchQuery, searchOverlayOpen]);

  const handleSuggestionClick = async (s) => {
    const text = suggestionDisplayText(s);
    const placeId = s?.placePrediction?.placeId;
    const token = placesSessionTokenRef.current;

    if (text) setSearchQuery(text);
    closeSearchOverlay();

    if (!placeId) return;

    try {
      const res = await utilityService.getCoordinatesFromPlace({
        placeId,
        sessionToken: token,
      });
      const lat = res?.data?.latitude;
      const lng = res?.data?.longitude;
      if (typeof lat !== "number" || typeof lng !== "number") return;

      // Adding an address: the search is just a faster way to place the pin,
      // so reuse dropPinAt and skip the provider search entirely.
      if (addAddressMode) {
        await dropPinAt(lat, lng);
        return;
      }

      const next = { lat, lng };
      setCoords(next);
      setCenter(next);
      setZoom(LOCATED_ZOOM);
      fetchProvidersWithFallback(lat, lng);
      setIsFetchingAddress(true);
      try {
        const formatted = await reverseGeocode(lat, lng);
        if (formatted) setAddressData(formatted);
      } finally {
        setIsFetchingAddress(false);
      }
    } catch {
      // swallow — user can pick another suggestion or fall back to current location
    }
  };

  // Provider-map flow: a saved address is just a shortcut to its coordinates,
  // so mirror handleSuggestionClick — drop the pin there, close the overlay and
  // search providers around it. Addresses with no coordinates never reach here;
  // the list routes those to handleEditAddressStart instead.
  const handleSavedAddressLocationClick = async (addr) => {
    const lat = addr?.coordinates?.latitude;
    const lng = addr?.coordinates?.longitude;
    if (typeof lat !== "number" || typeof lng !== "number") return;

    setSearchQuery(
      [addr.line1, addr.line2, addr.city, addr.state].filter(Boolean).join(", ")
    );
    closeSearchOverlay();

    const next = { lat, lng };
    setCoords(next);
    setCenter(next);
    setZoom(LOCATED_ZOOM);
    fetchProvidersWithFallback(lat, lng);
    setIsFetchingAddress(true);
    try {
      const formatted = await reverseGeocode(lat, lng);
      if (formatted) setAddressData(formatted);
    } finally {
      setIsFetchingAddress(false);
    }
  };

  const mapsApiRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const circleRef = useRef(null);
  // Ionic can leave this page `ion-page-hidden` on re-entry, blanking it.
  const pageRef = useUnhidePageOnEnter();
  const [mapReady, setMapReady] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  useEffect(() => {
    if (searchOverlayOpen) {
      setMapReady(false);
      circleRef.current = null;
      mapInstanceRef.current = null;
      mapsApiRef.current = null;
    }
  }, [searchOverlayOpen]);

  useEffect(() => {
    setMapReady(false);
    circleRef.current = null;
    mapInstanceRef.current = null;
    mapsApiRef.current = null;
  }, [addAddressMode]);

  const requestProviders = async (lat, lng, radiusM) => {
    const body = {
      patientId: patientProfile.id,
      planId,
      serviceId: service.serviceId,
      serviceMode: service?.serviceModeDetails?.[0]?.modeName,
      geolocationFilter: { latitude: lat, longitude: lng, radius: radiusM },
    };
    const res = await navigoServices.getAllTimeSlot(body);
    return providersFromResponse(res);
  };

  const fetchProvidersAtRadius = async (
    lat,
    lng,
    radiusM,
    { useRichEmptyState = false } = {}
  ) => {
    if (!patientProfile?.id || !service?.serviceId || !planId) return;

    setLoadingProviders(true);
    setNoProvidersFound(false);
    setFallbackExhausted(false);
    setSelectedProvider(null);

    try {
      const list = await requestProviders(lat, lng, radiusM);
      setProviders(list);
      if (list.length === 0) {
        if (useRichEmptyState) {
          setFallbackExhausted(true);
        } else {
          setNoProvidersFound(true);
        }
      }
    } catch {
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  const fetchProvidersWithFallback = async (lat, lng) => {
    if (!patientProfile?.id || !service?.serviceId || !planId) return;

    setLoadingProviders(true);
    setFallbackInProgress(true);
    setNoProvidersFound(false);
    setFallbackExhausted(false);
    setProviders([]);
    setSelectedProvider(null);

    const minStepMs = 1000;

    for (const radiusM of RADIUS_FALLBACKS_M) {
      setRadiusKm(radiusM / 1000);
      try {
        const [list] = await Promise.all([
          requestProviders(lat, lng, radiusM),
          new Promise((resolve) => setTimeout(resolve, minStepMs)),
        ]);
        if (list.length > 0) {
          setProviders(list);
          setLoadingProviders(false);
          setFallbackInProgress(false);
          return;
        }
      } catch {
        // try next radius
      }
    }

    setFallbackExhausted(true);
    setLoadingProviders(false);
    setFallbackInProgress(false);
  };

  const fetchLocation = async () => {
    setError(null);
    setIsLocating(true);
    try {
      const result = await getCurrentLocation({
        patientId: patientProfile?.id,
      });
      const next = { lat: result.lat, lng: result.lng };
      setCoords(next);
      setCurrentLocationCoords(next);
      setCenter(next);
      setZoom(LOCATED_ZOOM);
      setIsFetchingAddress(true);
      try {
        const formatted = await reverseGeocode(next.lat, next.lng);
        if (formatted) setAddressData(formatted);
      } finally {
        setIsFetchingAddress(false);
      }
      // Skip the provider search while picking a location for an address.
      if (!addAddressMode && !addressOnly) {
        fetchProvidersWithFallback(next.lat, next.lng);
      }
    } catch (err) {
      setError(userMessageForLocationError(err));
    } finally {
      setIsLocating(false);
    }
  };

  // Reset the search on the way OUT as well as on the way in. The entry reset
  // below only helps if the view is re-entered through a path that fires
  // willEnter; on iOS the page can come back without it, which left the
  // previous query's suggestions rendered above the saved addresses.
  useIonViewWillLeave(() => {
    setSearchQuery("");
    setSuggestions([]);
  });

  useIonViewWillEnter(() => {
    // Address-only mode (Home sample): show the saved-addresses list, no map
    // or geolocation prompt on entry. Always refresh the list on view-enter.
    if (addressOnly) {
      setAddAddressMode(null);
      setAddressForm(initialAddressForm);
      setEditingAddressId(null);
      setSaveError(null);
      setAddressMenuOpenId(null);
      setSearchQuery("");
      setSuggestions([]);
      setSearchOverlayOpen(true);
      setServiceError(null);
      setLoadingAddresses(true);
      familyPlanServices
        .getAllAddress(-1)
        .then((res) =>
          setSavedAddresses(Array.isArray(res?.data) ? res.data : [])
        )
        .catch(() => setSavedAddresses([]))
        .finally(() => setLoadingAddresses(false));
      return;
    }

    // Preserve state on back navigation (POP, e.g. returning from
    // /providerCard). Only run a full reset on a fresh entry (PUSH/REPLACE),
    // or when explicitly forced (e.g. "Search location on map" from filters,
    // which can re-enter this cached view as a POP).
    const forceMapReset = store.getState().navigo?.ui?.forceMapReset;
    if (history.action === "POP" && !forceMapReset) return;
    // Fresh maps session: clear any previously applied filter so the manual
    // (state/city/locality) screen starts empty for this new session.
    dispatch(
      setStateFromPayload({
        forceMapReset: false,
        centreFilter: null,
      })
    );

    setCoords(null);
    setCurrentLocationCoords(null);
    setCenter(DEFAULT_CENTER);
    setZoom(DEFAULT_ZOOM);
    setRadiusKm(6);
    setCustomRangeOpen(false);
    setPendingCustomKm(30);
    setAddressData(null);
    setIsLocating(false);
    setError(null);
    setProviders([]);
    setSelectedProvider(null);
    setLoadingProviders(false);
    setNoProvidersFound(false);
    setFallbackExhausted(false);
    setFallbackInProgress(false);
    setSearchQuery("");
    setSearchOverlayOpen(false);
    setSavedAddresses([]);
    setLoadingAddresses(false);
    setAddressMenuOpenId(null);
    setSuggestions([]);
    setLoadingSuggestions(false);
    setAddAddressMode(null);
    setAddressForm(initialAddressForm);
    setIsSavingAddress(false);
    setSaveError(null);
    setIsFetchingAddress(false);
    setEditingAddressId(null);
    setAddressToRemove(null);
    setIsDeletingAddress(false);
    setDeleteError(null);
    setMapReady(false);
    setMapKey((k) => k + 1);

    placesSessionTokenRef.current = null;
    mapsApiRef.current = null;
    mapInstanceRef.current = null;
    circleRef.current = null;

    fetchLocation();
  });

  const handleMapApiLoaded = ({ map, maps }) => {
    mapInstanceRef.current = map;
    mapsApiRef.current = maps;
    setMapReady(true);
  };

  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = mapsApiRef.current;
    if (!map || !maps || !coords) return;

    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    if (addAddressMode) {
      setCenter(coords);
      setZoom(LOCATED_ZOOM);
      return;
    }

    circleRef.current = new maps.Circle({
      map,
      center: coords,
      radius: radiusKm * 1000,
      fillColor: "#3B82F6",
      fillOpacity: 0.12,
      strokeColor: "#3B82F6",
      strokeOpacity: 0.5,
      strokeWeight: 1,
      clickable: false,
    });

    const mapDiv = typeof map.getDiv === "function" ? map.getDiv() : null;
    // Fall back to viewport-based sizing if the map div hasn't been measured
    // yet, so the auto-zoom is never silently skipped.
    const w = mapDiv?.offsetWidth || window.innerWidth || 360;
    const h =
      mapDiv?.offsetHeight ||
      Math.round((window.innerHeight || 640) * 0.5) ||
      320;
    const padding = 80;
    const effective = Math.max(Math.min(w, h) - 2 * padding, 120);
    const earthCircumferenceAtLat =
      40075016.686 * Math.cos((coords.lat * Math.PI) / 180);
    const radiusMeters = radiusKm * 1000;
    // Floor, not round: rounding can zoom past the exact fit and clip the
    // circle, and it was rounding the 10km and 15km fits onto the same level
    // (10.43 and 9.85 -> 10). Flooring keeps the circle inside the padding and,
    // at typical phone map sizes, keeps the presets on distinct levels
    // (11/10/9). Adjacent presets are under one zoom level apart, so at some
    // viewport sizes two of them will still share a level - the radius circle
    // is what conveys the difference then.
    const targetZoom = Math.floor(
      Math.log2((earthCircumferenceAtLat * effective) / (512 * radiusMeters))
    );
    // Drive the view imperatively as the single source of truth, and let
    // onChange keep the controlled center/zoom props tracking the map. Setting
    // the zoom/center STATE here too made google-map-react run its own
    // prop-driven animation alongside this one — two competing animations a
    // beat apart, which is the jerky "zoom then correct" two-step.
    const applyView = () => {
      if (typeof map.setZoom === "function") map.setZoom(targetZoom);
      if (typeof map.setCenter === "function") map.setCenter(coords);
    };
    applyView();
    // Re-apply once on idle as a backstop for the load race (google-map-react
    // applies its initial props after the map mounts). It is a no-op once the
    // map has settled, so it adds no second movement on radius changes.
    if (maps?.event && typeof maps.event.addListenerOnce === "function") {
      maps.event.addListenerOnce(map, "idle", applyView);
    }
  }, [coords, radiusKm, mapReady, addAddressMode]);

  // A geolocation failure ("Your location is unavailable...") is only about the
  // automatic lookup. Once a location has been established some other way —
  // search suggestion, map tap, saved address — the banner is stale, so clear
  // it whenever coords change. Every manual path funnels through setCoords, and
  // a failure that leaves coords untouched (e.g. the Current location button
  // erroring after a pin is already dropped) does not re-run this.
  useEffect(() => {
    if (coords) setError(null);
  }, [coords]);

  const handleMapClick = async ({ lat, lng }) => {
    // Dismiss-first: if a provider popup is open, the first map tap just
    // closes it. A subsequent tap drops the pin.
    if (selectedProvider) {
      setSelectedProvider(null);
      return;
    }

    const next = { lat, lng };
    setCoords(next);
    setCenter(next);
    if (!addAddressMode) {
      fetchProvidersWithFallback(lat, lng);
    }
    setIsFetchingAddress(true);
    try {
      const formatted = await reverseGeocode(lat, lng);
      setAddressData(formatted || null);
    } finally {
      setIsFetchingAddress(false);
    }
  };

  const handleAddNewAddressStart = async () => {
    closeSearchOverlay();
    setSearchQuery("");
    // PB: skip the pin-drop step entirely and go straight to the typed form.
    // Returning before the geolocation calls below also means PB never
    // triggers a permission prompt on this screen.
    if (hideLocationFeatures) {
      setAddAddressMode("form");
      return;
    }
    setAddAddressMode("pin");
    if (currentLocationCoords) {
      setCoords(currentLocationCoords);
      setCenter(currentLocationCoords);
      setZoom(LOCATED_ZOOM);
      setIsFetchingAddress(true);
      try {
        const formatted = await reverseGeocode(
          currentLocationCoords.lat,
          currentLocationCoords.lng
        );
        if (formatted) setAddressData(formatted);
      } finally {
        setIsFetchingAddress(false);
      }
    } else {
      // No cached current location (e.g. address-only / Home sample entry).
      // Fetch it for the pin only — do NOT run the provider fallback loop.
      setIsLocating(true);
      try {
        const result = await getCurrentLocation({
          patientId: patientProfile?.id,
        });
        setCurrentLocationCoords({ lat: result.lat, lng: result.lng });
        await dropPinAt(result.lat, result.lng);
      } catch (err) {
        setError(userMessageForLocationError(err));
      } finally {
        setIsLocating(false);
      }
    }
  };

  const handleEnterFullAddress = () => {
    if (!editingAddressId) {
      const fullName =
        patientProfile?.fullName ||
        [patientProfile?.firstName, patientProfile?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
      setAddressForm({
        name: fullName || "",
        phone: patientProfile?.phoneno ? String(patientProfile.phoneno) : "",
        housing: addressData?.housing || addressData?.building || "",
        colony:
          addressData?.street ||
          addressData?.subLocality ||
          addressData?.localityArea ||
          "",
        landmark: addressData?.landmark || "",
        pincode:
          addressData?.pincode != null ? String(addressData.pincode) : "",
        city: addressData?.city || "",
        district: addressData?.district || "",
        state: addressData?.state || "",
        addressType: "Home",
      });
    }
    setAddAddressMode("form");
  };

  const handleExitAddAddress = () => {
    setAddAddressMode(null);
    setAddressForm(initialAddressForm);
    setEditingAddressId(null);
    setSaveError(null);
    // The pin step has its own search box. Leaving it reopens the overlay
    // below, which would otherwise still be showing that query's suggestions —
    // this is an in-page transition, so the view-enter/leave resets never run.
    setSearchQuery("");
    setSuggestions([]);
  };

  const dropPinAt = async (lat, lng) => {
    const next = { lat, lng };
    setCoords(next);
    setCenter(next);
    setZoom(LOCATED_ZOOM);
    setIsFetchingAddress(true);
    try {
      const formatted = await reverseGeocode(lat, lng);
      if (formatted) setAddressData(formatted);
    } finally {
      setIsFetchingAddress(false);
    }
  };

  const handleEditAddressStart = async (addr) => {
    if (!addr?.id) return;
    closeSearchOverlay();
    setSearchQuery("");
    setEditingAddressId(addr.id);
    setSaveError(null);
    setAddressForm({
      name: addr.name || "",
      phone: addr.phoneNo != null ? String(addr.phoneNo) : "",
      housing: addr.line1 || "",
      colony: addr.line2 || "",
      landmark: addr.landmark || "",
      pincode: addr.pincode != null ? String(addr.pincode) : "",
      city: addr.city || "",
      district: addr.district || "",
      state: addr.state || "",
      addressType: addr.type || "Home",
    });
    setAddAddressMode("pin");

    if (!addressNeedsCoordinatesUpdate(addr)) {
      await dropPinAt(addr.coordinates.latitude, addr.coordinates.longitude);
      return;
    }

    try {
      const res = await addressService.getCoordinatesFromAddress(addr.id);
      const lat = res?.data?.latitude;
      const lng = res?.data?.longitude;
      if (typeof lat === "number" && typeof lng === "number") {
        await dropPinAt(lat, lng);
      } else if (currentLocationCoords) {
        setCoords(currentLocationCoords);
        setCenter(currentLocationCoords);
      }
    } catch {
      if (currentLocationCoords) {
        setCoords(currentLocationCoords);
        setCenter(currentLocationCoords);
      }
    }
  };

  const updateAddressForm = (field, value) => {
    // The banner describes the payload the server rejected, so it is stale the
    // moment the user changes anything. Clearing it here also frees the space
    // it was permanently occupying above the keyboard.
    setSaveError(null);
    setAddressForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirmRemoveAddress = async () => {
    if (!addressToRemove?.id || isDeletingAddress) return;
    setDeleteError(null);
    setIsDeletingAddress(true);
    try {
      await addressService.deleteAddress(addressToRemove.id);
      let updatedList = [];
      try {
        const res = await familyPlanServices.getAllAddress(-1);
        updatedList = Array.isArray(res?.data) ? res.data : [];
      } catch {
        // keep updatedList empty; user can refresh
      }
      setSavedAddresses(updatedList);
      setAddressToRemove(null);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't remove address. Please try again.";
      setDeleteError(message);
    } finally {
      setIsDeletingAddress(false);
    }
  };

  const cancelRemoveAddress = () => {
    if (isDeletingAddress) return;
    setAddressToRemove(null);
    setDeleteError(null);
  };

  useEffect(() => {
    if (!addressMenuOpenId) return undefined;
    const handler = (e) => {
      const inMenu = addressMenuRef.current?.contains(e.target);
      const inButton = activeTripleDotRef.current?.contains(e.target);
      if (!inMenu && !inButton) {
        setAddressMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [addressMenuOpenId]);

  const handleUpdateAddress = async () => {
    if (
      isSavingAddress ||
      !isAddressFormValid ||
      (!coords && !hideLocationFeatures) ||
      !editingAddressId
    ) {
      return;
    }

    setSaveError(null);
    setIsSavingAddress(true);

    const body = {
      name: addressForm.name.trim(),
      phoneNo: parseInt(addressForm.phone.replace(/\D/g, ""), 10),
      countryCode: 91,
      line1: addressForm.housing.trim(),
      line2: addressForm.colony.trim(),
      landmark: addressForm.landmark.trim(),
      district: addressForm.district.trim(),
      state: addressForm.state.trim(),
      city: addressForm.city.trim(),
      pincode: parseInt(addressForm.pincode.replace(/\D/g, ""), 10),
      patientId: patientProfile?.id,
      isDefault: false,
      type: addressForm.addressType,
      // (0,0) when there is no location — the PB path. That is the same
      // "needs resolving" marker addressNeedsCoordinatesUpdate() already
      // recognises, so the address is backfilled from its text later rather
      // than being saved with a wrong position.
      coordinates: {
        latitude: coords?.lat ?? 0,
        longitude: coords?.lng ?? 0,
      },
    };

    try {
      await addressService.updateAddress(editingAddressId, body);

      let updatedList = [];
      try {
        const res = await familyPlanServices.getAllAddress(-1);
        updatedList = Array.isArray(res?.data) ? res.data : [];
      } catch {
        // keep updatedList empty
      }
      setSavedAddresses(updatedList);
      addressesFetchedRef.current = true;

      setAddressForm(initialAddressForm);
      setEditingAddressId(null);
      setAddAddressMode(null);
      setSearchQuery("");
      setSuggestions([]);
      placesSessionTokenRef.current = makePlacesSessionToken();
      setSearchOverlayOpen(true);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't update address. Please try again.";
      setSaveError(message);
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleSaveAddress = async () => {
    // PB has no map and no search, so there is no `coords` to require. See
    // hideLocationFeatures.
    if (isSavingAddress || !isAddressFormValid) return;
    if (!coords && !hideLocationFeatures) return;

    setSaveError(null);
    setIsSavingAddress(true);

    const body = {
      name: addressForm.name.trim(),
      phoneNo: parseInt(addressForm.phone.replace(/\D/g, ""), 10),
      countryCode: 91,
      line1: addressForm.housing.trim(),
      line2: addressForm.colony.trim(),
      landmark: addressForm.landmark.trim(),
      district: addressForm.district.trim(),
      state: addressForm.state.trim(),
      city: addressForm.city.trim(),
      pincode: parseInt(addressForm.pincode.replace(/\D/g, ""), 10),
      patientId: patientProfile?.id,
      isDefault: false,
      type: addressForm.addressType,
      // (0,0) when there is no location — the PB path. That is the same
      // "needs resolving" marker addressNeedsCoordinatesUpdate() already
      // recognises, so the address is backfilled from its text later rather
      // than being saved with a wrong position.
      coordinates: {
        latitude: coords?.lat ?? 0,
        longitude: coords?.lng ?? 0,
      },
    };

    try {
      await familyPlanServices.postAddress(body, false);

      let updatedList = [];
      try {
        const res = await familyPlanServices.getAllAddress(-1);
        updatedList = Array.isArray(res?.data) ? res.data : [];
      } catch {
        // keep updatedList empty; overlay will still open
      }
      setSavedAddresses(updatedList);
      addressesFetchedRef.current = true;

      setAddressForm(initialAddressForm);
      setAddAddressMode(null);
      setSearchQuery("");
      setSuggestions([]);
      placesSessionTokenRef.current = makePlacesSessionToken();
      setSearchOverlayOpen(true);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't save address. Please try again.";
      setSaveError(message);
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleSelectRadius = (km) => {
    setRadiusKm(km);
    if (coords) {
      fetchProvidersAtRadius(coords.lat, coords.lng, km * 1000, {
        useRichEmptyState: true,
      });
    }
  };

  const openCustomRange = () => {
    const initial = RADIUS_PRESETS_KM.includes(radiusKm)
      ? 30
      : CUSTOM_RANGE_STOPS_KM.includes(radiusKm)
      ? radiusKm
      : 30;
    setPendingCustomKm(initial);
    setCustomRangeOpen(true);
  };

  const cancelCustomRange = () => {
    setCustomRangeOpen(false);
  };

  const applyCustomRange = () => {
    setCustomRangeOpen(false);
    setRadiusKm(pendingCustomKm);
    if (coords) {
      fetchProvidersAtRadius(coords.lat, coords.lng, pendingCustomKm * 1000, {
        useRichEmptyState: true,
      });
    }
  };

  const handleContinue = () => {
    if (!coords) return;

    const pickedLocation = {
      lat: coords.lat,
      lng: coords.lng,
      radiusKm,
      address: formatAddressLine(addressData),
      addressData,
    };
    // Picking on the map makes the active filter maps-based again,
    // clearing any previously applied manual (state/city/locality) filter.
    dispatch(
      setStateFromPayload({
        pickedLocation,
        centreFilter: { mode: "maps", radiusKm },
      })
    );

    // Providers were already fetched for this radius via getAllTimeSlot;
    // show them in the order the backend returned them.
    dispatch(setProvidersList(providers));
    history.push({
      pathname: "/providerCard",
      state: withFrom(routeLocation, { service, pageType }),
    });
  };

  const isCustomActive = !RADIUS_PRESETS_KM.includes(radiusKm);
  const isAddressFormValid = Boolean(
    addressForm.name.trim() &&
      addressForm.phone.trim() &&
      addressForm.housing.trim() &&
      addressForm.colony.trim() &&
      addressForm.pincode.trim()
  );
  const isAtCurrentLocation =
    coords &&
    currentLocationCoords &&
    coords.lat === currentLocationCoords.lat &&
    coords.lng === currentLocationCoords.lng;
  const customChipLabel = isCustomActive
    ? `Custom: ${radiusKm} km`
    : "Custom range";

  return (
    <IonPage ref={pageRef}>
      <IonContent className="no-keyboard-offset">
        {checkingServiceAddressId && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
            }}
          >
            <CircularProgress size={34} sx={{ color: "#97144D" }} />
            <p
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 700,
                color: "#97144D",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              Checking serviceability for this address…
            </p>
          </div>
        )}
        {navigoFinalSection ? (
          <NavigoFinalSectionContent
            memberRelation={memberRelation}
            service={service}
            price={price}
            addressId={choosedAddressId}
            familyMember={familyMember}
            pageType={pageType}
            onBack={() => setNavigoFinalSection(false)}
          />
        ) : (
          <AddressFormShell active={addAddressMode === "form"}>
          <div
            style={{
              fontFamily: "'Montserrat', sans-serif",
              backgroundColor: "#FFFFFF",
              // The address form is a plain block, exactly like this app's other
              // forms (familyInfo/components/AddFamilyMember.js and its
              // .add-family-member-container: padding, fields, a button at the
              // end, nothing else). No height, no flex column, no inner
              // scroller — IonContent scrolls it. Everything this screen kept
              // trying instead is what produced the clipped fields and the white
              // strip below Save.
              //
              // The map and provider-list modes do need a viewport-height
              // column, because the map fills whatever is left.
              //
              // `minHeight` (not `height`) is the whole trick. It is a floor,
              // not a cap: on a short phone viewport the form is taller than
              // the screen, grows past it and IonContent scrolls it — the
              // clipping this screen used to suffer came from `height: 100%`,
              // which is a cap. On a tall viewport the form is shorter than the
              // screen, and without a floor the white block just stopped under
              // Save and left the page background showing beneath it.
              ...(addAddressMode === "form"
                ? { minHeight: "100%" }
                : {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }),
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "16px",
                borderBottom: "1px solid #F1F5F9",
              }}
            >
              <button
                type="button"
                disabled={isSavingAddress}
                onClick={() => {
                  if (isSavingAddress) return;
                  if (addAddressMode === "form") {
                    // PB has no pin step, so "back" from the form cannot go
                    // there — it would render an empty screen (header, no
                    // body) and need a second back to escape. Leave
                    // add-address altogether instead. No search overlay on the
                    // way out either; PB does not have one.
                    if (hideLocationFeatures) {
                      handleExitAddAddress();
                      return;
                    }
                    setAddAddressMode("pin");
                  } else if (addAddressMode === "pin") {
                    // Searching inside the pin step: back closes the search
                    // first, rather than abandoning the new address.
                    if (searchOverlayOpen) {
                      setSearchQuery("");
                      closeSearchOverlay();
                      return;
                    }
                    handleExitAddAddress();
                    if (addressOnly) setSearchOverlayOpen(true);
                  } else if (searchOverlayOpen && !addressOnly) {
                    // Close the search first — but only on the provider/map
                    // flow, where the search overlay is a visibly distinct
                    // screen. In address-only (home sample) the overlay sits
                    // over the saved-address list and closing it changes almost
                    // nothing on screen, so back looked dead while actually
                    // doing something. Home sample always leaves instead.
                    setSearchQuery("");
                    closeSearchOverlay();
                  } else if (addressOnly) {
                    // Drop the query and its suggestions before leaving, so this
                    // screen can never come back showing a stale result list.
                    setSearchQuery("");
                    setSuggestions([]);
                    closeSearchOverlay();
                    // Navigate explicitly instead of history.goBack(). Popping
                    // took the WebView out of our SPA document entirely — the
                    // screen went blank and even a plain setInterval stopped
                    // running, so no JS of ours was alive to recover. This route
                    // is only ever reached from /bookingFor, and that screen
                    // reads its service from redux rather than route state.
                    // Straight to /bookingFor, not through the trail.
                    // /select-address is pushed from exactly one place
                    // (bookingForSection), so its parent is never in doubt —
                    // whereas the trail could still hold a /select-location
                    // from earlier in the session, and following it dropped the
                    // home-sample flow onto the provider map ("Search your
                    // location") with a back button that then had nowhere to go.
                    //
                    // backToPath still forces /bookingFor, but rewinds the trail
                    // to it instead of dropping it. A bare push handed
                    // /bookingFor a state with no trail, so ITS back button fell
                    // through to the fallback "/navigo" — bare, no query string —
                    // and an autologin session opened at
                    // /navigo?currScreen=1&serviceType=2&pageType=3 landed on the
                    // wrong service list.
                    backToPath(history, routeLocation, "/bookingFor", {
                      service,
                      pageType,
                      planId,
                    });
                  } else {
                    // Same reasoning as the addressOnly branch above: never pop.
                    // Reached from the service list (single-mode) or /select-mode,
                    // both recorded in state.from by routeForServiceMode.
                    goBackTo(history, routeLocation, "/navigo", {
                      service,
                      pageType,
                      planId,
                    });
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "#97144D",
                  display: "flex",
                  alignItems: "center",
                }}
                aria-label="Back"
              >
                <ArrowLeft size={22} />
              </button>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#12194C",
                }}
              >
                {addAddressMode
                  ? "Add new address"
                  : addressOnly
                  ? "Select address"
                  : "Search your location"}
              </h2>
            </div>

            <div style={{ padding: "12px 16px 8px" }}>
              {!hideLocationFeatures &&
                (addAddressMode === "pin" ||
                  (!addAddressMode && !addressOnly)) && (
                <div style={{ position: "relative", marginBottom: 12 }}>
                  <Search
                    size={18}
                    style={{
                      position: "absolute",
                      left: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#6B7280",
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={openSearchOverlay}
                    placeholder="Search your area"
                    style={{
                      width: "100%",
                      padding: "12px 40px 12px 42px",
                      fontSize: 14,
                      border: "1px solid #E5E7EB",
                      borderRadius: 10,
                      outline: "none",
                      fontFamily: "'Montserrat', sans-serif",
                      backgroundColor: "#FFFFFF",
                      boxSizing: "border-box",
                    }}
                  />
                  {(searchQuery || searchOverlayOpen) && (
                    <button
                      type="button"
                      onClick={handleSearchClear}
                      aria-label={searchOverlayOpen ? "Close" : "Clear search"}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#6B7280",
                        padding: 4,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              )}

              {/* Radius chips are a provider-search control: home sample picks a
                  saved address and has no radius. Guarded on addressOnly for
                  the same reason as the map block below — this was the other
                  half of the location UI leaking into the home-sample flow. */}
              {!searchOverlayOpen && !addressOnly && !addAddressMode && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    paddingBottom: 4,
                  }}
                >
                  {RADIUS_PRESETS_KM.map((km) => {
                    const active = radiusKm === km;
                    return (
                      <button
                        key={km}
                        type="button"
                        onClick={() => handleSelectRadius(km)}
                        disabled={fallbackInProgress}
                        style={{
                          flex: "0 0 auto",
                          padding: "6px 14px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: fallbackInProgress ? "default" : "pointer",
                          border: "1px solid #97144D",
                          color: active ? "#FFFFFF" : "#97144D",
                          backgroundColor: active ? "#97144D" : "#FFFFFF",
                        }}
                      >
                        {km} km
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={openCustomRange}
                    style={{
                      flex: "0 0 auto",
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "1px solid #97144D",
                      color: isCustomActive ? "#FFFFFF" : "#97144D",
                      backgroundColor: isCustomActive ? "#97144D" : "#FFFFFF",
                    }}
                  >
                    {customChipLabel}
                  </button>
                </div>
              )}
            </div>

            {/* Also in address-only mode, whatever the overlay is doing. The
                saved-address list lives in here, and home sample has nothing
                else to show — with the map and radius chips correctly hidden
                for that flow, an overlay-only condition left the screen empty
                the moment the overlay closed. */}
            {/* Everything in here is either the suggestion list (needs a
                non-empty query) or the saved-address list and its heading (both
                gated on !addAddressMode). So while adding an address it renders
                nothing — but it still carried `flex: 1` and took half the
                column, which pushed the pin-drop map into the bottom of the
                screen with a blank band above it. Only render it when it has
                something to show: the search overlay, or the saved-address
                list screen. */}
            {(searchOverlayOpen || (addressOnly && !addAddressMode)) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  padding: "0 16px 16px",
                }}
              >
                {searchQuery.trim() !== "" && (
                  <div
                    style={{
                      maxHeight: "40vh",
                      overflowY: "auto",
                      marginBottom: 14,
                    }}
                  >
                    {loadingSuggestions ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "16px 0",
                          gap: 10,
                        }}
                      >
                        <CircularProgress size={16} sx={{ color: "#97144D" }} />
                        <span style={{ fontSize: 13, color: "#6B7280" }}>
                          Searching…
                        </span>
                      </div>
                    ) : suggestions.length === 0 ? (
                      <p
                        style={{
                          margin: 0,
                          padding: "16px 8px",
                          fontSize: 13,
                          color: "#6B7280",
                          textAlign: "center",
                        }}
                      >
                        No matches found.
                      </p>
                    ) : (
                      <div
                        style={{
                          border: "1px solid #E5E7EB",
                          borderRadius: 12,
                          overflow: "hidden",
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        {suggestions.map((s, idx) => (
                          <button
                            key={
                              s?.placePrediction?.placeId ||
                              s?.placePrediction?.place ||
                              idx
                            }
                            type="button"
                            onClick={() => handleSuggestionClick(s)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              padding: "14px 16px",
                              border: "none",
                              borderTop:
                                idx === 0 ? "none" : "1px solid #F1F5F9",
                              background: "transparent",
                              cursor: "pointer",
                              textAlign: "left",
                              fontFamily: "'Montserrat', sans-serif",
                            }}
                          >
                            <MapPin
                              size={18}
                              style={{ color: "#97144D", flexShrink: 0 }}
                            />
                            <span
                              style={{
                                marginLeft: 12,
                                fontSize: 14,
                                color: "#12194C",
                                wordBreak: "break-word",
                              }}
                            >
                              {suggestionDisplayText(s)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!addressOnly && !addAddressMode && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        closeSearchOverlay();
                        setSearchQuery("");
                        fetchLocation();
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        padding: "14px 16px",
                        marginBottom: 10,
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        cursor: "pointer",
                        fontFamily: "'Montserrat', sans-serif",
                        textAlign: "left",
                      }}
                    >
                      <Navigation
                        size={20}
                        style={{ color: "#97144D", flexShrink: 0 }}
                      />
                      <span
                        style={{
                          flex: 1,
                          marginLeft: 12,
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#97144D",
                        }}
                      >
                        Use my current location
                      </span>
                      <ChevronRight size={18} style={{ color: "#97144D" }} />
                    </button>

                    <button
                      type="button"
                      onClick={handleAddNewAddressStart}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        padding: "14px 16px",
                        marginBottom: 18,
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        cursor: "pointer",
                        fontFamily: "'Montserrat', sans-serif",
                        textAlign: "left",
                      }}
                    >
                      <Plus
                        size={20}
                        style={{ color: "#97144D", flexShrink: 0 }}
                      />
                      <span
                        style={{
                          flex: 1,
                          marginLeft: 12,
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#97144D",
                        }}
                      >
                        Add a new address
                      </span>
                      <ChevronRight size={18} style={{ color: "#97144D" }} />
                    </button>
                  </>
                )}

                {serviceError && (
                  <div
                    style={{
                      margin: "0 0 12px",
                      padding: "10px 12px",
                      fontSize: 13,
                      color: "#B91C1C",
                      backgroundColor: "#FEF2F2",
                      border: "1px solid #FCA5A5",
                      borderRadius: 6,
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    {serviceError}
                  </div>
                )}

                {/* Adding an address: the overlay is search-only, so the saved
                  list and its heading stay hidden. */}
                {!addAddressMode && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      margin: "0 4px 12px",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#12194C",
                      }}
                    >
                      Saved addresses
                    </h3>
                    {addressOnly && (
                      <button
                        type="button"
                        onClick={handleAddNewAddressStart}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#97144D",
                          fontFamily: "'Montserrat', sans-serif",
                        }}
                      >
                        <Plus size={16} style={{ color: "#97144D" }} />
                        Add address
                      </button>
                    )}
                  </div>
                )}

                {!addAddressMode && (
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      paddingBottom: addressOnly
                        ? "calc(64px + env(safe-area-inset-bottom, 0px))"
                        : 8,
                    }}
                  >
                    {loadingAddresses ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "24px 0",
                          gap: 10,
                        }}
                      >
                        <CircularProgress size={18} sx={{ color: "#97144D" }} />
                        <span style={{ fontSize: 13, color: "#6B7280" }}>
                          Loading addresses…
                        </span>
                      </div>
                    ) : savedAddresses.length === 0 ? (
                      <p
                        style={{
                          margin: 0,
                          padding: "24px 8px",
                          fontSize: 13,
                          color: "#6B7280",
                          textAlign: "center",
                        }}
                      >
                        You don't have any saved addresses yet.
                      </p>
                    ) : (
                      savedAddresses.map((addr) => {
                        // The tag invites the user to re-pin an address that
                        // has no coordinates. PB has no map to re-pin with, so
                        // it would be a dead end — and every PB address is
                        // saved without coordinates by design.
                        const needsUpdate =
                          !hideLocationFeatures &&
                          addressNeedsCoordinatesUpdate(addr);
                        return (
                          <div
                            key={addr.id}
                            onClick={
                              needsUpdate
                                ? () => handleEditAddressStart(addr)
                                : addressOnly
                                ? () => handleSelectAddress(addr)
                                : () => handleSavedAddressLocationClick(addr)
                            }
                            style={{
                              position: "relative",
                              display: "flex",
                              gap: 14,
                              padding: 14,
                              marginBottom: 12,
                              border: "1px solid #E5E7EB",
                              borderRadius: 12,
                              backgroundColor: "#FFFFFF",
                              cursor: "pointer",
                            }}
                          >
                            <Home
                              size={26}
                              style={{
                                color: "#12194C",
                                flexShrink: 0,
                                marginTop: 2,
                              }}
                            />
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                paddingRight: 24,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#12194C",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {addr.name}
                                </p>
                                {needsUpdate && (
                                  <span
                                    style={{
                                      padding: "3px 10px",
                                      backgroundColor: "#FBE7ED",
                                      color: "#97144D",
                                      borderRadius: 999,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      fontFamily: "'Montserrat', sans-serif",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Update address
                                  </span>
                                )}
                              </div>
                              <p
                                style={{
                                  margin: "6px 0 8px",
                                  fontSize: 13,
                                  color: "#374151",
                                  lineHeight: 1.45,
                                  wordBreak: "break-word",
                                }}
                              >
                                {[addr.line1, addr.line2, addr.landmark]
                                  .filter(Boolean)
                                  .join(", ")}
                                <br />
                                {[addr.district, addr.city, addr.state]
                                  .filter(Boolean)
                                  .join(", ")}
                                {addr.pincode ? ` - ${addr.pincode}` : ""}
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 13,
                                  color: "#374151",
                                }}
                              >
                                Phone number- {addr.phoneNo}
                              </p>
                            </div>
                            <button
                              type="button"
                              ref={
                                addressMenuOpenId === addr.id
                                  ? activeTripleDotRef
                                  : null
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddressMenuOpenId(
                                  addressMenuOpenId === addr.id ? null : addr.id
                                );
                              }}
                              aria-label="More options"
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                background: "transparent",
                                border: "none",
                                padding: 4,
                                cursor: "pointer",
                                color: "#6B7280",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {addressMenuOpenId === addr.id && (
                              <div
                                ref={addressMenuRef}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: "absolute",
                                  top: 36,
                                  right: 10,
                                  backgroundColor: "#FFFFFF",
                                  border: "1px solid #E5E7EB",
                                  borderRadius: 8,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                  overflow: "hidden",
                                  zIndex: 5,
                                  minWidth: 120,
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAddressMenuOpenId(null);
                                    handleEditAddressStart(addr);
                                  }}
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    border: "none",
                                    background: "transparent",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#12194C",
                                    fontFamily: "'Montserrat', sans-serif",
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAddressMenuOpenId(null);
                                    setDeleteError(null);
                                    setAddressToRemove(addr);
                                  }}
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    border: "none",
                                    borderTop: "1px solid #F1F5F9",
                                    background: "transparent",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#B91C1C",
                                    fontFamily: "'Montserrat', sans-serif",
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Two different screens share this map, and address-only mode wants
                one of them but not the other:
                  - addAddressMode === "pin"  -> "Add new address". The pin-drop
                    IS how you add an address, home sample included. Must show.
                  - addAddressMode === null   -> "Select address", the saved-
                    address list. Home sample picks an existing address here; it
                    has no radius and no provider map. Must NOT show.
                This block used to be hidden only as a side effect of
                addressOnly opening the search overlay, so the moment that
                overlay closed the map appeared under the "Select address"
                heading. The fix for that was a blanket `!addressOnly`, which
                also removed the map from "Add new address" and left home sample
                with nothing but the manual search box. Gate on the mode instead
                of on addressOnly alone. */}
            {!hideLocationFeatures &&
              !searchOverlayOpen &&
              (!addressOnly || addAddressMode === "pin") &&
              addAddressMode !== "form" && (
                <>
                  <div
                    style={{
                      position: "relative",
                      flex: 1,
                      minHeight: 0,
                      margin: "0 16px",
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    {!apiKey ? (
                      <div
                        style={{
                          padding: 16,
                          color: "#6B7280",
                          textAlign: "center",
                        }}
                      >
                        Google Maps API key missing. Set
                        REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.
                      </div>
                    ) : (
                      <GoogleMapReact
                        key={mapKey}
                        bootstrapURLKeys={{ key: apiKey }}
                        center={center}
                        zoom={zoom}
                        onClick={handleMapClick}
                        onChange={({ center: c, zoom: z }) => {
                          // Track the map so the controlled props follow it (prevents
                          // google-map-react from re-applying a stale prop / fighting
                          // the imperative view changes).
                          if (c) setCenter(c);
                          if (typeof z === "number") setZoom(z);
                        }}
                        yesIWantToUseGoogleMapApiInternals
                        onGoogleApiLoaded={handleMapApiLoaded}
                        options={{
                          fullscreenControl: false,
                          mapTypeControl: false,
                          streetViewControl: false,
                          zoomControl: true,
                        }}
                      >
                        {coords && <Pin lat={coords.lat} lng={coords.lng} />}
                        {!addAddressMode &&
                          providers.map((p, idx) => {
                            const c = providerCoordsOf(p);
                            if (!c) return null;
                            const isSelected =
                              selectedProvider &&
                              (p.id != null
                                ? selectedProvider.id === p.id
                                : selectedProvider === p);
                            return (
                              <ProviderMarker
                                key={p.id ?? idx}
                                lat={c.lat}
                                lng={c.lng}
                                provider={p}
                                selected={isSelected}
                                onClick={() => setSelectedProvider(p)}
                                onClose={() => setSelectedProvider(null)}
                              />
                            );
                          })}
                      </GoogleMapReact>
                    )}

                    {isLocating && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          gap: 8,
                          backgroundColor: "rgba(255,255,255,0.7)",
                          color: "#12194C",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        <CircularProgress
                          size={28}
                          style={{ color: "#97144D" }}
                        />
                        Locating you…
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={fetchLocation}
                      disabled={isLocating}
                      style={{
                        position: "absolute",
                        bottom: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 18px",
                        backgroundColor: isAtCurrentLocation
                          ? "#97144D"
                          : "#FFFFFF",
                        border: "1px solid #97144D",
                        color: isAtCurrentLocation ? "#FFFFFF" : "#97144D",
                        borderRadius: 999,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: isLocating ? "not-allowed" : "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      }}
                    >
                      <Crosshair size={16} />
                      Current location
                    </button>
                  </div>

                  {error && (
                    <div
                      style={{
                        margin: "12px 16px 0",
                        padding: "8px 12px",
                        fontSize: 13,
                        color: "#B91C1C",
                        backgroundColor: "#FEF2F2",
                        border: "1px solid #FCA5A5",
                        borderRadius: 6,
                      }}
                    >
                      {error}
                    </div>
                  )}

                  {loadingProviders && coords && (
                    <div
                      style={{
                        padding: "28px 16px 32px",
                        marginTop: 12,
                        borderTop: "1px solid #F1F5F9",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 14,
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: "#97144D" }} />
                      <p
                        style={{
                          margin: 0,
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#12194C",
                          textAlign: "center",
                        }}
                      >
                        Searching providers near you within {radiusKm} km…
                      </p>
                    </div>
                  )}

                  {!loadingProviders &&
                    (fallbackExhausted ? (
                      <div
                        style={{
                          padding: "20px 16px 16px",
                          marginTop: 12,
                          borderTop: "1px solid #F1F5F9",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            backgroundColor: "#FEE2E2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Info size={26} style={{ color: "#DC2626" }} />
                        </div>
                        <h3
                          style={{
                            margin: "4px 0 0 0",
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#12194C",
                            textAlign: "center",
                          }}
                        >
                          Unable to find service providers!
                        </h3>
                        <p
                          style={{
                            margin: 0,
                            width: "100%",
                            fontSize: 13,
                            color: "#6B7280",
                            textAlign: "center",
                            lineHeight: 1.5,
                          }}
                        >
                          Unable to find service providers on selected location
                          in{" "}
                          {RADIUS_FALLBACKS_M[RADIUS_FALLBACKS_M.length - 1] /
                            1000}{" "}
                          kms range. Either you can set a custom range to search
                          again or you can select location manually.
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            width: "100%",
                            marginTop: 8,
                          }}
                        >
                          <button
                            type="button"
                            onClick={openCustomRange}
                            style={{
                              flex: 1,
                              padding: "14px 16px",
                              backgroundColor: "#F3F4F6",
                              color: "#374151",
                              border: "none",
                              borderRadius: 8,
                              fontSize: 14,
                              fontWeight: 700,
                              cursor: "pointer",
                              fontFamily: "'Montserrat', sans-serif",
                            }}
                          >
                            Custom range
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              history.push({
                                pathname: "/centre-filters",
                                state: withFrom(routeLocation, {
                                  service,
                                  pageType,
                                  initialMode: "manual",
                                  fromMaps: true,
                                }),
                              })
                            }
                            style={{
                              flex: 1,
                              padding: "14px 16px",
                              backgroundColor: "#97144D",
                              color: "#FFFFFF",
                              border: "none",
                              borderRadius: 8,
                              fontSize: 14,
                              fontWeight: 700,
                              cursor: "pointer",
                              fontFamily: "'Montserrat', sans-serif",
                            }}
                          >
                            Select manually
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: 16,
                          marginTop: 12,
                          borderTop: "1px solid #F1F5F9",
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                        }}
                      >
                        {coords && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 12,
                            }}
                          >
                            <Navigation
                              size={22}
                              color="#97144D"
                              fill="#97144D"
                              style={{ flexShrink: 0, marginTop: 2 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {isFetchingAddress ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "2px 0",
                                  }}
                                >
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: "#97144D" }}
                                  />
                                  <span
                                    style={{
                                      fontSize: 13,
                                      color: "#6B7280",
                                    }}
                                  >
                                    Fetching address…
                                  </span>
                                </div>
                              ) : (
                                <>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: 15,
                                      fontWeight: 700,
                                      color: "#12194C",
                                    }}
                                  >
                                    {addressData?.city || "Selected location"}
                                  </p>
                                  <p
                                    style={{
                                      margin: "4px 0 0 0",
                                      fontSize: 13,
                                      color: "#6B7280",
                                      lineHeight: 1.4,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {formatAddressLine(addressData) ||
                                      `${coords.lat.toFixed(
                                        5
                                      )}, ${coords.lng.toFixed(5)}`}
                                  </p>
                                </>
                              )}
                            </div>
                            <MapPinSolidIcon
                              style={{
                                width: 22,
                                height: 22,
                                color: "#97144D",
                                flexShrink: 0,
                                marginTop: 2,
                              }}
                            />
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={
                            addAddressMode === "pin"
                              ? handleEnterFullAddress
                              : handleContinue
                          }
                          disabled={!coords}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            backgroundColor: coords ? "#97144D" : "#D1D5DB",
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 8,
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: coords ? "pointer" : "not-allowed",
                            fontFamily: "'Montserrat', sans-serif",
                          }}
                        >
                          {addAddressMode === "pin"
                            ? "Enter full address"
                            : "Continue"}
                        </button>
                      </div>
                    ))}
                </>
              )}

            {!searchOverlayOpen && addAddressMode === "form" && (
              <div>
                {/* Map preview and its "Change" control are one unit: Change
                    sends the user back to the pin step, which PB does not
                    have. */}
                {!hideLocationFeatures && (
                <>
                <div
                  style={{
                    position: "relative",
                    height: 180,
                    margin: "0 16px",
                    borderRadius: 12,
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                    flexShrink: 0,
                  }}
                >
                  {/* Static preview: no gestures and no radius circle, so it
                    deliberately does NOT run handleMapApiLoaded — that would
                    repoint mapInstanceRef away from the interactive map for a
                    thumbnail that never uses it. */}
                  {apiKey && coords && (
                    <GoogleMapReact
                      bootstrapURLKeys={{ key: apiKey }}
                      center={center}
                      zoom={zoom}
                      options={{
                        fullscreenControl: false,
                        mapTypeControl: false,
                        streetViewControl: false,
                        zoomControl: false,
                        gestureHandling: "none",
                        clickableIcons: false,
                      }}
                    >
                      <Pin lat={coords.lat} lng={coords.lng} />
                    </GoogleMapReact>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "14px 16px",
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <MapPinSolidIcon
                    style={{
                      width: 20,
                      height: 20,
                      color: "#97144D",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#12194C",
                      }}
                    >
                      {addressData?.city || "Selected location"}
                    </p>
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: 13,
                        color: "#6B7280",
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                      }}
                    >
                      {formatAddressLine(addressData) ||
                        (coords
                          ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`
                          : "")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAddAddressMode("pin")}
                    disabled={isSavingAddress}
                    style={{
                      padding: "6px 14px",
                      backgroundColor: "#97144D",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "'Montserrat', sans-serif",
                      flexShrink: 0,
                    }}
                  >
                    Change
                  </button>
                </div>
                </>
                )}

                <div
                  style={{
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                  }}
                >
                  <TextField
                    label="Full name"
                    required
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    disabled={isSavingAddress}
                    value={addressForm.name}
                    onChange={(e) => updateAddressForm("name", e.target.value)}
                  />
                  <TextField
                    label="Phone number"
                    required
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    disabled={isSavingAddress}
                    value={addressForm.phone}
                    onChange={(e) => updateAddressForm("phone", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <span
                          style={{
                            color: "#9CA3AF",
                            marginRight: 8,
                            fontSize: 14,
                            fontFamily: "'Montserrat', sans-serif",
                          }}
                        >
                          +91
                        </span>
                      ),
                    }}
                  />
                  <TextField
                    label="House No. / Floor / Building"
                    required
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    disabled={isSavingAddress}
                    value={addressForm.housing}
                    onChange={(e) =>
                      updateAddressForm("housing", e.target.value)
                    }
                  />
                  <TextField
                    label="Colony / Street / Area"
                    required
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    disabled={isSavingAddress}
                    value={addressForm.colony}
                    onChange={(e) =>
                      updateAddressForm("colony", e.target.value)
                    }
                  />
                  <TextField
                    label="Landmark"
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    disabled={isSavingAddress}
                    value={addressForm.landmark}
                    onChange={(e) =>
                      updateAddressForm("landmark", e.target.value)
                    }
                  />
                  <TextField
                    label="Pincode"
                    required
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    disabled={isSavingAddress}
                    value={addressForm.pincode}
                    onChange={(e) =>
                      updateAddressForm("pincode", e.target.value)
                    }
                  />
                  <div style={{ display: "flex", gap: 12 }}>
                    <TextField
                      label="City"
                      size="small"
                      fullWidth
                      sx={FORM_FIELD_SX}
                      disabled={isSavingAddress}
                      value={addressForm.city}
                      onChange={(e) =>
                        updateAddressForm("city", e.target.value)
                      }
                    />
                    <TextField
                      label="District"
                      size="small"
                      fullWidth
                      sx={FORM_FIELD_SX}
                      disabled={isSavingAddress}
                      value={addressForm.district}
                      onChange={(e) =>
                        updateAddressForm("district", e.target.value)
                      }
                    />
                  </div>
                  <TextField
                    label="State"
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    disabled={isSavingAddress}
                    value={addressForm.state}
                    onChange={(e) => updateAddressForm("state", e.target.value)}
                  />

                  <p
                    style={{
                      margin: "4px 0 4px",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#12194C",
                    }}
                  >
                    Address type<span style={{ color: "#DC2626" }}>*</span>
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { value: "Home", icon: Home },
                      { value: "Work", icon: Briefcase },
                      { value: "Other", icon: MapPin },
                    ].map(({ value, icon: Icon }) => {
                      const active = addressForm.addressType === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() =>
                            updateAddressForm("addressType", value)
                          }
                          disabled={isSavingAddress}
                          style={{
                            flex: 1,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            padding: "10px 12px",
                            border: active
                              ? "1px solid #97144D"
                              : "1px solid #E5E7EB",
                            backgroundColor: active ? "#FBE7ED" : "#FFFFFF",
                            color: active ? "#97144D" : "#6B7280",
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "'Montserrat', sans-serif",
                          }}
                        >
                          <Icon size={16} />
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div
                  style={{
                    // Plain in-flow bar at the end of the form. Not sticky and not
                    // fixed: anything pinned here floats over the fields the moment
                    // the viewport changes (focusing a field, keyboard opening).
                    padding:
                      "16px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
                    borderTop: "1px solid #F1F5F9",
                  }}
                >
                  {saveError && (
                    <div
                      style={{
                        marginBottom: 12,
                        padding: "10px 12px",
                        fontSize: 13,
                        color: "#B91C1C",
                        backgroundColor: "#FEF2F2",
                        border: "1px solid #FCA5A5",
                        borderRadius: 6,
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      {saveError}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={
                      editingAddressId ? handleUpdateAddress : handleSaveAddress
                    }
                    disabled={!isAddressFormValid || isSavingAddress}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      backgroundColor:
                        isAddressFormValid && !isSavingAddress
                          ? "#97144D"
                          : "#D1D5DB",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 700,
                      cursor:
                        isAddressFormValid && !isSavingAddress
                          ? "pointer"
                          : "not-allowed",
                      fontFamily: "'Montserrat', sans-serif",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    {isSavingAddress && (
                      <CircularProgress
                        size={16}
                        thickness={5}
                        sx={{ color: "#FFFFFF" }}
                      />
                    )}
                    {isSavingAddress
                      ? editingAddressId
                        ? "Updating…"
                        : "Saving…"
                      : editingAddressId
                      ? "Update address"
                      : "Save address"}
                  </button>
                </div>
              </div>
            )}

            {addressToRemove && (
              <>
                <div
                  className="navigo-sheet-backdrop"
                  onClick={cancelRemoveAddress}
                  style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.45)",
                    zIndex: 1000,
                  }}
                />
                <div
                  className="navigo-sheet-slide-up"
                  style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "#FFFFFF",
                    padding: "24px 20px 20px",
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    zIndex: 1001,
                    fontFamily: "'Montserrat', sans-serif",
                    boxShadow: "0 -6px 20px rgba(0,0,0,0.12)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        backgroundColor: "#FBE7ED",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MapPinOff size={26} style={{ color: "#97144D" }} />
                    </div>
                    <h3
                      style={{
                        margin: "18px 0 0",
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#12194C",
                        textAlign: "center",
                      }}
                    >
                      Remove address?
                    </h3>
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 13,
                        color: "#6B7280",
                        textAlign: "center",
                        lineHeight: 1.5,
                      }}
                    >
                      Selected address will be completely removed from the
                      address book.
                    </p>
                  </div>

                  {deleteError && (
                    <div
                      style={{
                        marginTop: 14,
                        padding: "10px 12px",
                        fontSize: 13,
                        color: "#B91C1C",
                        backgroundColor: "#FEF2F2",
                        border: "1px solid #FCA5A5",
                        borderRadius: 6,
                      }}
                    >
                      {deleteError}
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      marginTop: 24,
                    }}
                  >
                    <button
                      type="button"
                      onClick={cancelRemoveAddress}
                      disabled={isDeletingAddress}
                      style={{
                        flex: 1,
                        padding: "14px 16px",
                        backgroundColor: "#F3F4F6",
                        color: "#374151",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: isDeletingAddress ? "not-allowed" : "pointer",
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmRemoveAddress}
                      disabled={isDeletingAddress}
                      style={{
                        flex: 1,
                        padding: "14px 16px",
                        backgroundColor: "#FEE2E2",
                        color: "#B91C1C",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: isDeletingAddress ? "not-allowed" : "pointer",
                        fontFamily: "'Montserrat', sans-serif",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      {isDeletingAddress && (
                        <CircularProgress
                          size={14}
                          thickness={5}
                          sx={{ color: "#B91C1C" }}
                        />
                      )}
                      {isDeletingAddress ? "Removing…" : "Remove"}
                    </button>
                  </div>
                </div>
              </>
            )}

            {customRangeOpen && (
              <>
                <div
                  className="navigo-sheet-backdrop"
                  onClick={cancelCustomRange}
                  style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.45)",
                    zIndex: 1000,
                  }}
                />
                <div
                  className="navigo-sheet-slide-up"
                  style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "#FFFFFF",
                    padding: "24px 20px 20px",
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    zIndex: 1001,
                    fontFamily: "'Montserrat', sans-serif",
                    boxShadow: "0 -6px 20px rgba(0,0,0,0.12)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#FBE7ED",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MapPin size={22} style={{ color: "#97144D" }} />
                    </div>
                    <h3
                      style={{
                        margin: "18px 0 0",
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#12194C",
                      }}
                    >
                      Choose custom range
                    </h3>
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 13,
                        color: "#6B7280",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Higher the km, higher the chance of getting more service
                      providers
                    </p>
                  </div>

                  <div
                    style={{
                      position: "relative",
                      margin: "32px 8px 28px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: 8,
                        right: 8,
                        height: 2,
                        backgroundColor: "#E5E7EB",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        position: "relative",
                      }}
                    >
                      {CUSTOM_RANGE_STOPS_KM.map((km) => {
                        const isSelected = pendingCustomKm === km;
                        return (
                          <div
                            key={km}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              flex: 1,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => setPendingCustomKm(km)}
                              aria-label={`${km} km`}
                              style={{
                                width: isSelected ? 22 : 12,
                                height: isSelected ? 22 : 12,
                                borderRadius: "50%",
                                backgroundColor: "#97144D",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                marginTop: isSelected ? 0 : 5,
                                marginBottom: isSelected ? 0 : 5,
                                boxShadow: isSelected
                                  ? "0 0 0 4px rgba(151,20,77,0.18)"
                                  : "none",
                              }}
                            />
                            <span
                              style={{
                                marginTop: 12,
                                fontSize: 13,
                                fontWeight: 700,
                                color: "#12194C",
                              }}
                            >
                              {km}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                color: "#6B7280",
                              }}
                            >
                              km
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={applyCustomRange}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      backgroundColor: "#97144D",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    Continue
                  </button>
                </div>
              </>
            )}
          </div>
          </AddressFormShell>
        )}
      </IonContent>
    </IonPage>
  );
}
